'use client';
import React, { useRef, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { CardGridItem, isCardOutOfStock, getCardDiscount, CardLocale, getCardTitle } from '../../types/card';
import { CountryFlags } from '../CountryFlags/CountryFlags';
import StarRating from '../ProductDetailsSection/StarRating';
import { CardImage } from '../ui/CardImage/CardImage';
import { getTitleSuffix } from '@/constants';
import { useTranslations, useLocale } from 'next-intl';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import { useAuth } from '../../context/AuthContext';
import { useWishlist } from '../../context/WishlistContext';
import { FavoriteIcon, FavoriteBorderIcon } from '../icons';
import styles from './ProductCardGrid.module.css';

const WishlistHeart = React.memo<{ productId: string }>(({ productId }) => {
  const tCommon = useTranslations('common.buttons');
  const { user, isLoading: authLoading } = useAuth();
  const { isInWishlist, isHydrated, add, remove } = useWishlist();
  const { localizeFullPath } = useLocalizedPath();
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const active = !!user && isInWishlist(productId);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (authLoading || busy) return;
    if (!user) {
      router.push(localizeFullPath('/login'));
      return;
    }
    setBusy(true);
    try {
      if (active) await remove(productId);
      else await add(productId);
    } catch {
      // swallow - optimistic state already reverted in context
    } finally {
      setBusy(false);
    }
  };

  const disabled = busy || (!!user && !isHydrated);
  const label = active ? tCommon('removeFromWishlist') : tCommon('addToWishlist');

  return (
    <button
      type="button"
      className={`${styles.wishlistHeart}${active ? ` ${styles.active}` : ''}`}
      onClick={handleClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={active}
      title={label}
    >
      {active ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
    </button>
  );
});
WishlistHeart.displayName = 'WishlistHeart';

const ArrowBackSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M11.67 3.87 9.9 2.1 0 12l9.9 9.9 1.77-1.77L3.54 12z"/></svg>
);
const ArrowForwardSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M5.88 4.12 13.76 12l-7.88 7.88L7.65 21.65 17.3 12 7.65 2.35z"/></svg>
);


interface ProductCardGridProps {
  // CardGridItem, not Card: server components must pass cards through
  // toCardGridItem() so the RSC payload stays slim (ACEB-162). Full Card
  // arrays (client-side fetches) remain assignable.
  cards: CardGridItem[];
  spacing?: number;
  itemsPerRow?: {
    xs?: number;
    sm?: number;
    lg?: number;
  };
  slider?: boolean;
  maxCards?: number;
  autoSlide?: boolean;
  eagerLoadCount?: number;
  /** Whether eager cards also get a <head> image preload. Keep the default
   * (true) only for grids at the top of the viewport; pass false when the
   * grid sits below other content (e.g. homepage, under the hero) so the
   * preloads don't compete with that page's LCP element. */
  preloadEagerImages?: boolean;
  /** Stack cards in a single vertical column (for narrow sidebars). */
  vertical?: boolean;
}

export const ProductCardGrid: React.FC<ProductCardGridProps> = ({
  cards,
  spacing = 3,
  itemsPerRow = { xs: 6, sm: 6, lg: 3 },
  slider = false,
  maxCards,
  autoSlide = false,
  eagerLoadCount = 0,
  preloadEagerImages = true,
  vertical = false,
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('product');
  const locale = useLocale() as CardLocale;
  const { localizeFullPath } = useLocalizedPath();

  // Calculate displayCards before hooks
  const displayCards = cards && cards.length > 0 ? (maxCards ? cards.slice(0, maxCards) : cards) : [];

  // Dynamically calculate scroll amount based on actual card dimensions
  const getScrollAmount = () => {
    if (!sliderRef.current) return 300; // fallback
    
    const firstCard = sliderRef.current.querySelector('.product-card');
    if (!firstCard) return 300; // fallback
    
    const cardRect = firstCard.getBoundingClientRect();
    const cardWidth = cardRect.width;
    
    // Get the gap by measuring the distance between first and second card
    const secondCard = firstCard.nextElementSibling?.querySelector?.('.product-card');
    let gap = 16; // default gap
    
    if (secondCard) {
      const secondCardRect = secondCard.getBoundingClientRect();
      gap = secondCardRect.left - cardRect.right;
    }
    
    return cardWidth + gap;
  };

  // Auto-scroll functionality - must be called before any early returns
  useEffect(() => {
    if (!slider || !autoSlide || displayCards.length === 0) {
      return;
    }

    // Add delay to ensure cards are rendered and measured properly
    const startDelay = setTimeout(() => {
      if (!sliderRef.current) return;

      const interval = setInterval(() => {
        if (sliderRef.current && document.hasFocus()) {
          const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
          const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 10;

          if (isAtEnd) {
            // Loop back to the beginning
            sliderRef.current.scrollTo({
              left: 0,
              behavior: 'smooth'
            });
          } else {
            // Dynamically calculate scroll amount each time
            const scrollAmount = getScrollAmount();
            sliderRef.current.scrollBy({
              left: scrollAmount,
              behavior: 'smooth'
            });
          }
        }
      }, 3000);

      // Clean up interval when component unmounts
      return () => clearInterval(interval);
    }, 1500); // Slightly longer delay to ensure layout is stable

    return () => {
      clearTimeout(startDelay);
    };
  }, [slider, autoSlide, displayCards.length]);

  // Early return check - after all hooks
  if (!cards || cards.length === 0) {
    return null;
  }

  const scrollLeft = () => {
    if (sliderRef.current) {
      const scrollAmount = getScrollAmount();
      sliderRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (sliderRef.current) {
      const scrollAmount = getScrollAmount();
      sliderRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Helper function to render card content
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderCardContent = (card: CardGridItem, isSlider = false, _index = 0, isEager = false) => {
    const discount = getCardDiscount(card);
    const outOfStock = isCardOutOfStock(card);
    const hasDiscountRibbon = !outOfStock && discount > 0;

    const cardClass = isSlider ? styles.sliderCard : styles.cardItem;

    return (
      <Link href={localizeFullPath(`/giftcards/${card.slug}`)} style={{ textDecoration: 'none', color: 'inherit' }}>
        <div className={`${cardClass} product-card`}>
          <div className={styles.cardBackgroundGradient} />

          {/* Blurred background image using the same product logo */}
          {/* <CardBackgroundImage>
          <Image
            src={card?.logo || fullLogoBlack.src}
            alt=""
            fill
            loading="lazy"
            priority={false}
            unoptimized
            // style={{ objectFit: 'cover' }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = fullLogoBlack.src;
            }}
          />
        </CardBackgroundImage> */}

        <div className={styles.cardImage}>
          <CardImage
            card={card}
            locale={locale}
            size="grid"
            eager={isEager}
            preload={isEager && preloadEagerImages}
            className={styles.cardImageFloat}
          />
        </div>

        {card.countries && card.countries.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
            minHeight: '24px'
          }}>
            <CountryFlags
              countries={card.countries}
              maxVisible={3}
              flagSize={{ width: '16px', height: '12px' }}
            />
          </div>
        )}

        <WishlistHeart productId={card._id} />

        {hasDiscountRibbon && (
          <div
            className={styles.discountCornerRibbon}
            aria-label={`${discount}% ${t('pricing.off')}`}
          >
            <span className={styles.discountCornerRibbonStrip}>
              −{discount}%
            </span>
          </div>
        )}

        <div className={styles.cardInfo}>
          <h3 className={styles.cardTitle}>
            {`${getCardTitle(card, locale)}${getTitleSuffix()}`}
          </h3>

          {outOfStock && (
            <span className={`${styles.discountBadge} ${styles.outOfStockBadge}`}>
              {t('stock.outOfStock')}
            </span>
          )}

          <div className={styles.starRow}>
            <StarRating reviewScore={card.rating?.score} reviewCount={card.rating?.count} showReviewCount={false} />
          </div>
        </div>
        </div>
      </Link>
    );
  };

  // Render slider mode
  if (slider) {
    const fewCards = displayCards.length <= 3;

    return (
      <div className={styles.sliderContainer}>
        {!fewCards && (
          <button className={`${styles.navButton} ${styles.navButtonLeft}`} onClick={scrollLeft}>
            <ArrowBackSvg />
          </button>
        )}

        <div
          ref={sliderRef}
          className={`${styles.sliderTrack}${fewCards ? ` ${styles.sliderTrackCentered}` : ''}`}
        >
          {displayCards.map((card, index) => (
            <div key={card._id || card.slug}>
              {renderCardContent(card, true, index, index < eagerLoadCount)}
            </div>
          ))}
        </div>

        {!fewCards && (
          <button className={`${styles.navButton} ${styles.navButtonRight}`} onClick={scrollRight}>
            <ArrowForwardSvg />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`${styles.cardGrid}${vertical ? ` ${styles.cardGridVertical}` : ''} card-grid`}>
      {displayCards.map((card, index) => (
        <div key={card._id || card.slug}>
          {renderCardContent(card, false, index, index < eagerLoadCount)}
        </div>
      ))}
    </div>
  );
};
